interface JWTPayload {
  exp: number; // Expiration time (Unix timestamp)
  iat: number; // Issued at time
  hotel_id?: string;
  user_id?: string;
  reservation_id?: string;
  guest_id?: string;
  [key: string]: any;
}

interface TokenInfo {
  payload: JWTPayload;
  isExpired: boolean;
  isExpiringSoon: boolean; // Within 5 minutes
  expiresAt: Date;
  timeUntilExpiry: number; // Milliseconds
}

/**
 * Decode JWT token without verification (client-side only)
 */
export function decodeJWT(token: string): JWTPayload | null {
  try {
    // Split the token into parts
    const parts = token.split('.');
    if (parts.length !== 3) {
      console.warn('Invalid JWT token format');
      return null;
    }

    // Decode the payload (second part)
    const base64Url = parts[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    
    // Add padding if needed
    const padding = '='.repeat((4 - (base64.length % 4)) % 4);
    const paddedBase64 = base64 + padding;
    
    const jsonPayload = decodeURIComponent(
      atob(paddedBase64)
        .split('')
        .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );

    return JSON.parse(jsonPayload) as JWTPayload;
  } catch (error) {
    console.error('Error decoding JWT token:', error);
    return null;
  }
}

/**
 * Check if token is expired
 */
export function isTokenExpired(token: string): boolean {
  const payload = decodeJWT(token);
  if (!payload || !payload.exp) {
    return true; // Consider invalid tokens as expired
  }

  const currentTime = Math.floor(Date.now() / 1000);
  return payload.exp <= currentTime;
}

/**
 * Check if token is expiring soon (within specified minutes)
 */
export function isTokenExpiringSoon(token: string, warningMinutes: number = 5): boolean {
  const payload = decodeJWT(token);
  if (!payload || !payload.exp) {
    return true;
  }

  const currentTime = Math.floor(Date.now() / 1000);
  const warningTime = currentTime + (warningMinutes * 60);
  return payload.exp <= warningTime;
}

/**
 * Get comprehensive token information
 */
export function getTokenInfo(token: string): TokenInfo | null {
  const payload = decodeJWT(token);
  if (!payload || !payload.exp) {
    return null;
  }

  const currentTime = Math.floor(Date.now() / 1000);
  const isExpired = payload.exp <= currentTime;
  const isExpiringSoon = payload.exp <= (currentTime + 300); // 5 minutes
  const expiresAt = new Date(payload.exp * 1000);
  const timeUntilExpiry = Math.max(0, (payload.exp * 1000) - Date.now());

  return {
    payload,
    isExpired,
    isExpiringSoon,
    expiresAt,
    timeUntilExpiry,
  };
}

/**
 * Get time until token expires in human-readable format
 */
export function getTimeUntilExpiry(token: string): string {
  const tokenInfo = getTokenInfo(token);
  if (!tokenInfo) {
    return 'Invalid token';
  }

  if (tokenInfo.isExpired) {
    return 'Expired';
  }

  const minutes = Math.floor(tokenInfo.timeUntilExpiry / (1000 * 60));
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);

  if (days > 0) {
    return `${days} day${days !== 1 ? 's' : ''}`;
  } else if (hours > 0) {
    return `${hours} hour${hours !== 1 ? 's' : ''}`;
  } else if (minutes > 0) {
    return `${minutes} minute${minutes !== 1 ? 's' : ''}`;
  } else {
    return 'Less than a minute';
  }
}

/**
 * Extract hotel ID from JWT token
 */
export function getHotelIdFromToken(token: string): string | null {
  const payload = decodeJWT(token);
  return payload?.hotel_id || null;
}

/**
 * Extract reservation ID from JWT token
 */
export function getReservationIdFromToken(token: string): string | null {
  const payload = decodeJWT(token);
  return payload?.reservation_id || null;
}

/**
 * Extract guest ID from JWT token
 */
export function getGuestIdFromToken(token: string): string | null {
  const payload = decodeJWT(token);
  return payload?.guest_id || payload?.user_id || null;
}

/**
 * Extract booking reference from JWT token
 */
export function getBookingReferenceFromToken(token: string): string | null {
  const payload = decodeJWT(token);
  return payload?.booking_reference || payload?.reservation_id || null;
}

/**
 * Check if token is valid (not expired and properly formatted)
 */
export function isTokenValid(token: string): boolean {
  if (!token || typeof token !== 'string') {
    return false;
  }

  const tokenInfo = getTokenInfo(token);
  return tokenInfo !== null && !tokenInfo.isExpired;
}

/**
 * Get milliseconds until token should be refreshed (refresh 5 minutes before expiry)
 */
export function getRefreshTimeout(token: string): number {
  const tokenInfo = getTokenInfo(token);
  if (!tokenInfo) {
    return 0;
  }

  // Refresh 5 minutes before expiry, or immediately if already expired/expiring soon
  const refreshTime = Math.max(0, tokenInfo.timeUntilExpiry - (5 * 60 * 1000));
  return Math.min(refreshTime, 24 * 60 * 60 * 1000); // Max 24 hours
}

/**
 * Store token with metadata
 */
export function storeToken(token: string, storageKey: string = 'guest_token'): void {
  const tokenInfo = getTokenInfo(token);
  if (!tokenInfo) {
    console.warn('Attempting to store invalid token');
    return;
  }

  const tokenData = {
    token,
    storedAt: Date.now(),
    expiresAt: tokenInfo.expiresAt.getTime(),
    hotel_id: getHotelIdFromToken(token),
    reservation_id: getReservationIdFromToken(token),
    booking_reference: getBookingReferenceFromToken(token),
    guest_id: getGuestIdFromToken(token),
  };

  localStorage.setItem(storageKey, JSON.stringify(tokenData));
}

/**
 * Load token from storage with validation
 */
export function loadToken(storageKey: string = 'guest_token'): string | null {
  try {
    const storedData = localStorage.getItem(storageKey);
    if (!storedData) {
      return null;
    }

    const tokenData = JSON.parse(storedData);
    const token = tokenData.token;

    if (!token || !isTokenValid(token)) {
      // Remove invalid token
      localStorage.removeItem(storageKey);
      return null;
    }

    return token;
  } catch (error) {
    console.error('Error loading token from storage:', error);
    localStorage.removeItem(storageKey);
    return null;
  }
}

/**
 * Clear token from storage
 */
export function clearToken(storageKey: string = 'guest_token'): void {
  localStorage.removeItem(storageKey);
}